import { useState } from 'react';
import { Chip, CHIP_CATALOG } from '@/components/ui/Chip';
import { useToast } from '@/components/ui/Toast';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { Skeleton, TableRowSkeleton, CardSkeleton } from '@/components/ui/Skeleton';
import { EmptyState, CompactEmptyState } from '@/components/ui/EmptyState';
import { FormField } from '@/components/ui/FormField';

/* ── Section wrapper ── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 'var(--space-12)' }}>
      <h2 style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--color-text-muted)',
        letterSpacing: 'var(--tracking-caps)',
        textTransform: 'uppercase',
        marginBottom: 'var(--space-4)',
        paddingBottom: 'var(--space-3)',
        borderBottom: '1px solid var(--color-border-default)',
      }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

/* ── Sub-group label inside a section ── */

function SubLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <p style={{
      fontFamily: 'var(--font-sans)',
      fontSize: 'var(--text-xs)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--color-text-subtle)',
      textTransform: 'uppercase',
      letterSpacing: 'var(--tracking-wider)',
      marginBottom: 'var(--space-2)',
      marginTop: 'var(--space-5)',
      ...style,
    }}>
      {children}
    </p>
  );
}

/* ── Card wrapper ── */

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--card-border)',
      borderRadius: 'var(--card-radius)',
      padding: 'var(--card-padding)',
      boxShadow: 'var(--card-shadow)',
      ...style,
    }}>
      {children}
    </div>
  );
}

/* ── Primary button (reused in page) ── */

function Btn({ onClick, children, variant = 'primary' }: {
  onClick?: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'danger';
}) {
  const bgMap = {
    primary: 'var(--color-action-primary)',
    secondary: 'var(--color-action-secondary)',
    danger: 'var(--color-action-danger)',
  };
  const hoverMap = {
    primary: 'var(--color-action-primary-hover)',
    secondary: 'var(--color-action-secondary-hover)',
    danger: 'var(--color-action-danger-hover)',
  };
  const textMap = {
    primary: 'var(--color-action-primary-text)',
    secondary: 'var(--color-action-secondary-text)',
    danger: 'var(--color-action-danger-text)',
  };
  const borderMap = {
    primary: 'none',
    secondary: '1px solid var(--color-action-secondary-border)',
    danger: 'none',
  };

  return (
    <button
      onClick={onClick}
      style={{
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        border: borderMap[variant],
        backgroundColor: bgMap[variant],
        color: textMap[variant],
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        cursor: 'pointer',
        transition: 'background-color var(--transition-base)',
        letterSpacing: 'var(--tracking-wide)',
      }}
      onMouseEnter={e => (e.currentTarget.style.backgroundColor = hoverMap[variant])}
      onMouseLeave={e => (e.currentTarget.style.backgroundColor = bgMap[variant])}
    >
      {children}
    </button>
  );
}

/* ── Main page ── */

export function ComponentLibraryPage() {
  const { addToast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailValue, setEmailValue] = useState('');

  const chipGroups = ['Campaign', 'Domain', 'JT Sync', 'Inbox'];

  return (
    <div style={{ maxWidth: 'var(--layout-content-max)', paddingBottom: 'var(--space-16)' }}>
      {/* Page intro */}
      <div style={{ marginBottom: 'var(--space-10)' }}>
        <h1 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-4xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--color-text-brand)',
          marginBottom: 'var(--space-2)',
          letterSpacing: 'var(--tracking-tight)',
        }}>
          Component Library
        </h1>
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-md)',
          color: 'var(--color-text-muted)',
        }}>
          Screen 28 reference · JT Engage v1.0 · All values from{' '}
          <code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>styles.css</code>
        </p>
      </div>

      {/* ── 1. CHIPS ── */}
      <Section title="Status chips / badges">
        {chipGroups.map(group => {
          const items = CHIP_CATALOG.filter(c => c.group === group);
          return (
            <div key={group}>
              <SubLabel>{group}</SubLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {items.map(({ label, variant }) => (
                  <Chip key={variant} variant={variant}>{label}</Chip>
                ))}
              </div>
            </div>
          );
        })}
      </Section>

      {/* ── 2. TOASTS ── */}
      <Section title="Toasts / notifications">
        <Card>
          <p style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text-muted)',
            marginBottom: 'var(--space-4)',
          }}>
            Auto-dismiss after 8 s. Click to trigger: toasts appear bottom-right.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            <Btn onClick={() => addToast({
              variant: 'success',
              title: 'Campaign launched',
              description: 'All 342 recipients have been enrolled.',
            })}>
              Success toast
            </Btn>
            <Btn variant="secondary" onClick={() => addToast({
              variant: 'warning',
              title: 'Warming cap approaching',
              description: 'Your domain can send 80 more emails today.',
            })}>
              Warning toast
            </Btn>
            <Btn variant="danger" onClick={() => addToast({
              variant: 'danger',
              title: 'Send failed',
              description: 'Could not reach email provider. Retry in 5 min.',
            })}>
              Danger toast
            </Btn>
            <Btn variant="secondary" onClick={() => addToast({
              variant: 'info',
              title: 'JT sync running',
              description: 'Candidate list will update shortly.',
            })}>
              Info toast
            </Btn>
          </div>
        </Card>
      </Section>

      {/* ── 3. CONFIRM MODAL ── */}
      <Section title="Confirmation modal (destructive)">
        <Card>
          <p style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text-muted)',
            marginBottom: 'var(--space-4)',
          }}>
            Used for: delete campaign, remove team member, delete suppression rule.
          </p>
          <Btn variant="danger" onClick={() => setConfirmOpen(true)}>
            Delete campaign…
          </Btn>
        </Card>

        <ConfirmModal
          open={confirmOpen}
          title="Delete campaign?"
          description="This will permanently delete 'Q3 Candidate Outreach' and all its performance data. This action cannot be undone."
          confirmLabel="Delete campaign"
          cancelLabel="Keep campaign"
          variant="danger"
          onConfirm={() => {
            setConfirmOpen(false);
            addToast({ variant: 'success', title: 'Campaign deleted' });
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      </Section>

      {/* ── 4. SKELETON LOADERS ── */}
      <Section title="Loading skeletons">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
          <div>
            <SubLabel style={{ marginTop: 0 }}>Table rows (3 shown per spec)</SubLabel>
            <Card>
              <TableRowSkeleton rows={3} />
            </Card>
          </div>
          <div>
            <SubLabel style={{ marginTop: 0 }}>Card skeleton</SubLabel>
            <CardSkeleton />
          </div>
        </div>

        <SubLabel>Individual skeleton blocks</SubLabel>
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <Skeleton variant="title" width="40%" />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="75%" />
          <Skeleton variant="text" width="55%" />
        </Card>
      </Section>

      {/* ── 5. EMPTY STATES ── */}
      <Section title="Empty states">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
          <div>
            <SubLabel style={{ marginTop: 0 }}>Full empty state</SubLabel>
            <Card>
              <EmptyState
                icon={<IconInbox size={28} />}
                title="No replies yet"
                description="When candidates reply to your campaigns, their messages will appear here."
                action={
                  <Btn>Create your first campaign</Btn>
                }
              />
            </Card>
          </div>
          <div>
            <SubLabel style={{ marginTop: 0 }}>Workflow empty state</SubLabel>
            <Card style={{
              backgroundColor: 'var(--workflow-canvas-bg)',
              minHeight: '220px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <EmptyState
                icon={<IconPlus size={24} />}
                title="Add your first step"
                description="Drag a step from the panel or click the + trigger."
              />
            </Card>
          </div>
        </div>

        <SubLabel>Compact / inline empty state</SubLabel>
        <CompactEmptyState
          icon={<IconFile size={16} />}
          message="No suppression rules configured. Add rules to protect your sender reputation."
          action={
            <Btn variant="secondary">Add rule</Btn>
          }
        />
      </Section>

      {/* ── 6. FORM FIELDS ── */}
      <Section title="Form fields">
        <Card style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)', maxWidth: '680px' }}>
          {/* Normal state */}
          <FormField
            label="Campaign name"
            placeholder="e.g. Q3 Candidate Outreach"
            hint="This is shown in reports and the campaign list."
          />

          {/* Required */}
          <FormField
            label="From name"
            placeholder="Sienna Park"
            required
          />

          {/* Error state */}
          <FormField
            label="Reply-to email"
            type="email"
            placeholder="you@company.com"
            required
            value={emailValue}
            onChange={e => {
              setEmailValue(e.target.value);
              setEmailError(
                e.target.value && !e.target.value.includes('@')
                  ? 'Enter a valid email address.'
                  : ''
              );
            }}
            error={emailError || (emailValue === '' ? 'This field is required.' : '')}
          />

          {/* Disabled */}
          <FormField
            label="Organisation"
            value="Bridge Talent"
            disabled
            hint="Contact your admin to change."
          />
        </Card>
      </Section>

      {/* ── 7. TYPOGRAPHY SCALE ── */}
      <Section title="Typography scale">
        <Card>
          {([
            ['--text-6xl', '32px', 'Stat number headline'],
            ['--text-5xl', '28px', 'Dashboard metric'],
            ['--text-4xl', '24px', 'Page title'],
            ['--text-3xl', '20px', 'Small page title'],
            ['--text-2xl', '18px', 'Page subheading'],
            ['--text-xl',  '16px', 'Section heading'],
            ['--text-lg',  '15px', 'Subheading, card title'],
            ['--text-md',  '14px', 'Larger body'],
            ['--text-base','13px', 'Primary body text'],
            ['--text-sm',  '12px', 'Table cells, secondary body'],
            ['--text-xs',  '11px', 'Labels, timestamps'],
          ] as const).map(([token, size, desc]) => (
            <div key={token} style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--space-4)',
              paddingTop: 'var(--space-3)',
              paddingBottom: 'var(--space-3)',
              borderBottom: '1px solid var(--color-border-default)',
            }}>
              <span style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-subtle)',
                width: '120px',
                flexShrink: 0,
              }}>
                {token}
              </span>
              <span style={{ fontSize: `var(${token})`, color: 'var(--color-text-primary)', flex: 1 }}>
                The quick brown fox
              </span>
              <span style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-muted)',
                flexShrink: 0,
              }}>
                {size} · {desc}
              </span>
            </div>
          ))}
        </Card>
      </Section>

      {/* ── 8. COLOUR PALETTE ── */}
      <Section title="Brand colour palette">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          {[
            { label: 'Navy', token: 'var(--color-brand-navy)', textColor: 'white' },
            { label: 'Blue', token: 'var(--color-brand-blue)', textColor: 'white' },
            { label: 'Teal', token: 'var(--color-brand-teal)', textColor: 'white' },
            { label: 'Indigo', token: 'var(--color-brand-indigo)', textColor: 'white' },
            { label: 'Success', token: 'var(--color-success)', textColor: 'white' },
            { label: 'Warning', token: 'var(--color-warning)', textColor: 'white' },
            { label: 'Danger', token: 'var(--color-danger)', textColor: 'white' },
            { label: 'Info', token: 'var(--color-info)', textColor: 'white' },
          ].map(({ label, token, textColor }) => (
            <div key={label} style={{
              backgroundColor: token,
              borderRadius: 'var(--radius-input)',
              padding: 'var(--space-4) var(--space-5)',
              minWidth: '100px',
              textAlign: 'center',
            }}>
              <span style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: textColor,
              }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 9. SPACING SCALE ── */}
      <Section title="Spacing scale (4px increments)">
        <Card>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {[1,2,3,4,5,6,8,10,12,14,16].map(n => (
              <div key={n} style={{ textAlign: 'center' }}>
                <div style={{
                  width: `var(--space-${n})`,
                  height: `var(--space-${n})`,
                  backgroundColor: 'var(--color-brand-indigo)',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: 'var(--space-2)',
                }} />
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-muted)',
                }}>
                  {n}
                </span>
              </div>
            ))}
          </div>
        </Card>
      </Section>
    </div>
  );
}

/* ── Inline icons for the page ── */

function IconInbox({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
    </svg>
  );
}

function IconPlus({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function IconFile({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
}

